import styled from 'styled-components';
import { motion } from 'framer-motion';

export const AdminDashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

export const WelcomeSection = styled.div`
  margin-bottom: 2rem;
`;

export const WelcomeTitle = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
  text-transform: none !important;
  direction: rtl;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

export const WelcomeSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
`;

export const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

export const QuickActionCard = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 1.5rem;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  transition: var(--transition);
  cursor: pointer;

  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const QuickActionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

export const QuickActionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    switch (props.$color) {
      case 'primary': return 'linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%)';
      case 'success': return 'linear-gradient(135deg, var(--success-color) 0%, var(--success-dark) 100%)';
      case 'warning': return 'linear-gradient(135deg, var(--warning-color) 0%, var(--warning-dark) 100%)';
      case 'info': return 'linear-gradient(135deg, var(--info-color) 0%, var(--info-dark) 100%)';
      case 'danger': return 'linear-gradient(135deg, var(--error-color) 0%, var(--error-dark) 100%)';
      default: return 'linear-gradient(135deg, var(--text-muted) 0%, var(--text-secondary) 100%)';
    }
  }};
  color: var(--text-inverse);
  font-size: 1.25rem;
`;

export const QuickActionContent = styled.div`
  flex: 1;
`;

export const QuickActionTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  font-family: 'Inter', sans-serif;
`;

export const QuickActionDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.4;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
`;

export const StatCard = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 1.5rem;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  transition: var(--transition);

  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    switch (props.color) {
      case 'primary': return 'rgba(216, 184, 137, 0.1)';
      case 'success': return 'rgba(34, 197, 94, 0.1)';
      case 'warning': return 'rgba(245, 158, 11, 0.1)';
      case 'info': return 'rgba(59, 130, 246, 0.1)';
      case 'danger': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(107, 114, 128, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.color) {
      case 'primary': return 'var(--primary-color)';
      case 'success': return 'var(--success-color)';
      case 'warning': return 'var(--warning-color)';
      case 'info': return 'var(--info-color)';
      case 'danger': return 'var(--error-color)';
      default: return 'var(--text-muted)';
    }
  }};
`;

export const StatContent = styled.div`
  flex: 1;
`;

export const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
  font-family: 'Inter', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

export const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

export const FullWidthContainer = styled.div`
  background-color: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  margin-bottom: 2rem;
`;

export const Section = styled.div`
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const SectionHeader = styled.div`
  margin-bottom: 1.5rem;

  h2 {
    color: var(--text-primary);
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: 'Inter', sans-serif;

    @media (max-width: 768px) {
      font-size: 1.125rem;
    }
  }
`;

export const RecentActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--background-secondary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  transition: var(--transition);

  &:hover {
    background: var(--background-tertiary);
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
  }
`;

export const ActivityIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(216, 184, 137, 0.1);
  color: var(--primary-color);
  flex-shrink: 0;
`;

export const ActivityContent = styled.div`
  flex: 1;
`;

export const ActivityTitle = styled.div`
  color: var(--text-primary);
  font-weight: 500;
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
`;

export const ActivityTime = styled.div`
  color: var(--text-secondary);
  font-size: 0.75rem;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);

  svg {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;
